'use server'

import { apiRequest, ApiResponse } from '../request'
import {
    AllergyType,
    AllergySeverity,
    AllergyStatus,
    DiagnosisType,
    DiagnosisStatus,
    DiagnosisCertainty,
    ReferralUrgency,
    ReferralStatus,
    MedicalCertificateType,
    ConsentType,
} from '@mediardex/contracts'
import { downloadBlob } from '../../download-blob'
import { getStoredToken } from '@/lib/api/session-refresh'

export {
    AllergyType,
    AllergySeverity,
    AllergyStatus,
    DiagnosisType,
    DiagnosisStatus,
    DiagnosisCertainty,
    ReferralUrgency,
    ReferralStatus,
    MedicalCertificateType,
    ConsentType,
}

// ==================== ENUMS ====================

export enum PersonalHistoryCategory {
    CARDIOVASCULAR = 'CARDIOVASCULAR',
    RESPIRATORY = 'RESPIRATORY',
    NEUROLOGICAL = 'NEUROLOGICAL',
    ENDOCRINE = 'ENDOCRINE',
    GASTROINTESTINAL = 'GASTROINTESTINAL',
    RENAL = 'RENAL',
    HEPATIC = 'HEPATIC',
    MUSCULOSKELETAL = 'MUSCULOSKELETAL',
    DERMATOLOGICAL = 'DERMATOLOGICAL',
    PSYCHIATRIC = 'PSYCHIATRIC',
    ONCOLOGICAL = 'ONCOLOGICAL',
    INFECTIOUS = 'INFECTIOUS',
    AUTOIMMUNE = 'AUTOIMMUNE',
    SURGICAL = 'SURGICAL',
    HOSPITALIZATION = 'HOSPITALIZATION',
    TRAUMA = 'TRAUMA',
    GYNECOLOGICAL = 'GYNECOLOGICAL',
    OBSTETRIC = 'OBSTETRIC',
    VACCINATION = 'VACCINATION',
    TRANSFUSION = 'TRANSFUSION',
    OTHER = 'OTHER',
}

export enum ConditionStatus {
    ACTIVE = 'ACTIVE',
    CONTROLLED = 'CONTROLLED',
    RESOLVED = 'RESOLVED',
    IN_REMISSION = 'IN_REMISSION',
}

export enum FamilyRelationship {
    FATHER = 'FATHER',
    MOTHER = 'MOTHER',
    PATERNAL_GRANDFATHER = 'PATERNAL_GRANDFATHER',
    PATERNAL_GRANDMOTHER = 'PATERNAL_GRANDMOTHER',
    MATERNAL_GRANDFATHER = 'MATERNAL_GRANDFATHER',
    MATERNAL_GRANDMOTHER = 'MATERNAL_GRANDMOTHER',
    BROTHER = 'BROTHER',
    SISTER = 'SISTER',
    SON = 'SON',
    DAUGHTER = 'DAUGHTER',
    UNCLE = 'UNCLE',
    AUNT = 'AUNT',
    COUSIN = 'COUSIN',
    OTHER = 'OTHER',
}

export enum TimelineEventType {
    TIMELINE_CONSULTATION = 'TIMELINE_CONSULTATION',
    TIMELINE_TRIAGE = 'TIMELINE_TRIAGE',
    TIMELINE_EVOLUTION = 'TIMELINE_EVOLUTION',
    TIMELINE_PRESCRIPTION = 'TIMELINE_PRESCRIPTION',
    TIMELINE_EXAM_REQUEST = 'TIMELINE_EXAM_REQUEST',
    TIMELINE_EXAM_RESULT = 'TIMELINE_EXAM_RESULT',
    TIMELINE_PROCEDURE = 'TIMELINE_PROCEDURE',
    TIMELINE_DIAGNOSIS = 'TIMELINE_DIAGNOSIS',
    TIMELINE_ALLERGY_REGISTERED = 'TIMELINE_ALLERGY_REGISTERED',
    TIMELINE_REFERRAL = 'TIMELINE_REFERRAL',
    TIMELINE_CERTIFICATE = 'TIMELINE_CERTIFICATE',
    TIMELINE_REPORT = 'TIMELINE_REPORT',
    TIMELINE_DOCUMENT_SIGNED = 'TIMELINE_DOCUMENT_SIGNED',
    TIMELINE_HOSPITALIZATION = 'TIMELINE_HOSPITALIZATION',
    TIMELINE_DISCHARGE = 'TIMELINE_DISCHARGE',
    TIMELINE_VACCINATION = 'TIMELINE_VACCINATION',
    TIMELINE_NOTE = 'TIMELINE_NOTE',
}

export enum MedicalSpecialty {
    GENERAL_PRACTICE = 'GENERAL_PRACTICE',
    CARDIOLOGY = 'CARDIOLOGY',
    DERMATOLOGY = 'DERMATOLOGY',
    ENDOCRINOLOGY = 'ENDOCRINOLOGY',
    GASTROENTEROLOGY = 'GASTROENTEROLOGY',
    GERIATRICS = 'GERIATRICS',
    GYNECOLOGY = 'GYNECOLOGY',
    OBSTETRICS = 'OBSTETRICS',
    HEMATOLOGY = 'HEMATOLOGY',
    NEPHROLOGY = 'NEPHROLOGY',
    NEUROLOGY = 'NEUROLOGY',
    ONCOLOGY = 'ONCOLOGY',
    OPHTHALMOLOGY = 'OPHTHALMOLOGY',
    ORTHOPEDICS = 'ORTHOPEDICS',
    OTOLARYNGOLOGY = 'OTOLARYNGOLOGY',
    PEDIATRICS = 'PEDIATRICS',
    PNEUMOLOGY = 'PNEUMOLOGY',
    PSYCHIATRY = 'PSYCHIATRY',
    RHEUMATOLOGY = 'RHEUMATOLOGY',
    UROLOGY = 'UROLOGY',
    ANESTHESIOLOGY = 'ANESTHESIOLOGY',
    RADIOLOGY = 'RADIOLOGY',
    PATHOLOGY = 'PATHOLOGY',
    EMERGENCY = 'EMERGENCY',
    INTENSIVE_CARE = 'INTENSIVE_CARE',
    SURGERY_GENERAL = 'SURGERY_GENERAL',
    SURGERY_CARDIOVASCULAR = 'SURGERY_CARDIOVASCULAR',
    SURGERY_PLASTIC = 'SURGERY_PLASTIC',
    SURGERY_VASCULAR = 'SURGERY_VASCULAR',
    PHYSICAL_THERAPY = 'PHYSICAL_THERAPY',
    NUTRITION = 'NUTRITION',
    PSYCHOLOGY = 'PSYCHOLOGY',
    SPEECH_THERAPY = 'SPEECH_THERAPY',
    OCCUPATIONAL_THERAPY = 'OCCUPATIONAL_THERAPY',
    DENTISTRY = 'DENTISTRY',
    OTHER = 'OTHER',
}

// ==================== INTERFACES ====================

export interface Professional {
    id: number
    name: string
    email?: string
    registrationNumber?: string
    councilName?: string
}

export interface IcdCode {
    id: number
    code: string
    description: string
    chapter?: string
    groupCode?: string
    category?: string
}

// ---------- ALLERGY ----------

export interface Allergy {
    id: number
    clientId: number
    type: AllergyType
    allergen: string
    severity: AllergySeverity
    reaction?: string
    onsetDate?: string
    confirmedBy?: number
    status: AllergyStatus
    notes?: string
    companyId: number
    registeredById: number
    registeredBy?: Professional
    createdAt: string
    updatedAt: string
}

export interface CreateAllergyData {
    clientId: number
    companyId: number
    registeredById?: number
    type: AllergyType
    allergen: string
    severity: AllergySeverity
    reaction?: string
    onsetDate?: string
    status?: AllergyStatus
    notes?: string
}

export interface UpdateAllergyData {
    type?: AllergyType
    allergen?: string
    severity?: AllergySeverity
    reaction?: string
    onsetDate?: string
    status?: AllergyStatus
    notes?: string
}

// ---------- PERSONAL HISTORY ----------

export interface PersonalHistory {
    id: number
    clientId: number
    category: PersonalHistoryCategory
    condition: string
    icdCodeId?: number
    icdCode?: IcdCode
    diagnosisDate?: string
    resolutionDate?: string
    status: ConditionStatus
    severity?: string
    treatment?: string
    currentMedications?: string
    notes?: string
    companyId: number
    registeredById: number
    registeredBy?: Professional
    createdAt: string
    updatedAt: string
}

export interface CreatePersonalHistoryData {
    clientId: number
    companyId: number
    registeredById?: number
    category: PersonalHistoryCategory
    condition: string
    icdCodeId?: number
    diagnosisDate?: string
    resolutionDate?: string
    status?: ConditionStatus
    severity?: string
    treatment?: string
    currentMedications?: string
    notes?: string
}

export interface UpdatePersonalHistoryData {
    category?: PersonalHistoryCategory
    condition?: string
    icdCodeId?: number
    diagnosisDate?: string
    resolutionDate?: string
    status?: ConditionStatus
    severity?: string
    treatment?: string
    currentMedications?: string
    notes?: string
}

// ---------- FAMILY HISTORY ----------

export interface FamilyHistory {
    id: number
    clientId: number
    relationship: FamilyRelationship
    condition: string
    icdCodeId?: number
    icdCode?: IcdCode
    ageAtOnset?: number
    deceased: boolean
    deathAge?: number
    deathCause?: string
    notes?: string
    companyId: number
    registeredById: number
    registeredBy?: Professional
    createdAt: string
    updatedAt: string
}

export interface CreateFamilyHistoryData {
    clientId: number
    companyId: number
    registeredById?: number
    relationship: FamilyRelationship
    condition: string
    icdCodeId?: number
    ageAtOnset?: number
    deceased?: boolean
    deathAge?: number
    deathCause?: string
    notes?: string
}

export interface UpdateFamilyHistoryData {
    relationship?: FamilyRelationship
    condition?: string
    icdCodeId?: number
    ageAtOnset?: number
    deceased?: boolean
    deathAge?: number
    deathCause?: string
    notes?: string
}

// ---------- ANAMNESIS ----------

export interface AnamnesisTemplate {
    id: number
    name: string
    specialty: MedicalSpecialty
    description?: string
    structure: any
    companyId?: number
    isSystem: boolean
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface Anamnesis {
    id: number
    clientId: number
    professionalId: number
    professional?: Professional
    templateId?: number
    template?: AnamnesisTemplate
    consultationId?: number
    occupation?: string
    religion?: string
    informant?: string
    reliability?: string
    chiefComplaint?: string
    historyOfPresentIllness?: string
    systemsReview?: any
    lifeHabits?: any
    socialConditions?: any
    physicalExam?: any
    diagnosticHypothesis?: string
    data?: any
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateAnamnesisData {
    clientId: number
    companyId: number
    professionalId: number
    templateId?: number
    consultationId?: number
    occupation?: string
    religion?: string
    informant?: string
    reliability?: string
    chiefComplaint?: string
    historyOfPresentIllness?: string
    systemsReview?: any
    lifeHabits?: any
    socialConditions?: any
    physicalExam?: any
    diagnosticHypothesis?: string
    data?: any
}

export interface UpdateAnamnesisData {
    templateId?: number
    consultationId?: number
    occupation?: string
    religion?: string
    informant?: string
    reliability?: string
    chiefComplaint?: string
    historyOfPresentIllness?: string
    systemsReview?: any
    lifeHabits?: any
    socialConditions?: any
    physicalExam?: any
    diagnosticHypothesis?: string
    data?: any
}

// ---------- MEDICAL EVOLUTION ----------

export interface VitalSigns {
    temperature?: number
    heartRate?: number
    bloodPressure?: string
    bloodPressureSystolic?: number
    bloodPressureDiastolic?: number
    respiratoryRate?: number
    oxygenSaturation?: number
    weight?: number
    height?: number
    painLevel?: number
    bloodGlucose?: number
}

export interface MedicalEvolution {
    id: number
    clientId: number
    professionalId: number
    professional?: Professional
    consultationId?: number
    evolutionDate: string
    subjective?: string
    objective?: string
    vitalSigns?: VitalSigns
    assessment?: string
    plan?: string
    diagnosisIds?: number[]
    prescriptionId?: number
    examsRequested?: string
    referralId?: number
    returnDate?: string
    returnNotes?: string
    signedAt?: string
    signatureData?: any
    signatureValid?: boolean
    attachments?: any
    metadata?: any
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateEvolutionData {
    clientId: number
    companyId: number
    professionalId: number
    consultationId?: number
    evolutionDate?: string
    subjective?: string
    objective?: string
    vitalSigns?: VitalSigns
    assessment?: string
    plan?: string
    diagnosisIds?: number[]
    prescriptionId?: number
    examsRequested?: string
    referralId?: number
    returnDate?: string
    returnNotes?: string
}

export interface UpdateEvolutionData {
    consultationId?: number
    evolutionDate?: string
    subjective?: string
    objective?: string
    vitalSigns?: VitalSigns
    assessment?: string
    plan?: string
    diagnosisIds?: number[]
    prescriptionId?: number
    examsRequested?: string
    referralId?: number
    returnDate?: string
    returnNotes?: string
}

// ---------- DIAGNOSIS ----------

export interface Diagnosis {
    id: number
    clientId: number
    professionalId: number
    professional?: Professional
    icdCodeId?: number
    icdCode?: IcdCode
    customDiagnosis?: string
    type: DiagnosisType
    status: DiagnosisStatus
    certainty: DiagnosisCertainty
    diagnosisDate: string
    resolutionDate?: string
    notes?: string
    consultationId?: number
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateDiagnosisData {
    clientId: number
    companyId: number
    professionalId: number
    icdCodeId?: number
    customDiagnosis?: string
    type?: DiagnosisType
    status?: DiagnosisStatus
    certainty?: DiagnosisCertainty
    diagnosisDate?: string
    resolutionDate?: string
    notes?: string
    consultationId?: number
}

export interface UpdateDiagnosisData {
    icdCodeId?: number
    customDiagnosis?: string
    type?: DiagnosisType
    status?: DiagnosisStatus
    certainty?: DiagnosisCertainty
    diagnosisDate?: string
    resolutionDate?: string
    notes?: string
    consultationId?: number
}

// ---------- REFERRAL ----------

export interface Referral {
    id: number
    clientId: number
    referringProfessionalId: number
    referringProfessional?: Professional
    receivingProfessionalId?: number
    receivingProfessional?: Professional
    externalProfessionalName?: string
    externalProfessionalCrm?: string
    externalClinicName?: string
    externalClinicAddress?: string
    externalClinicPhone?: string
    partnerId?: number
    partner?: {
        id: number
        name: string
        tradingName?: string
        phone?: string
        email?: string
        street?: string
        number?: string
        neighborhood?: string
        city?: string
        state?: string
        zipCode?: string
    }
    specialty: MedicalSpecialty
    urgency: ReferralUrgency
    status: ReferralStatus
    reason: string
    clinicalSummary?: string
    relevantExams?: string
    currentMedications?: string
    diagnosisId?: number
    referralDate: string
    scheduledDate?: string
    completedDate?: string
    feedback?: string
    feedbackDate?: string
    attachments?: any
    signedAt?: string
    signatureData?: any
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateReferralData {
    clientId: number
    companyId: number
    referringProfessionalId: number
    receivingProfessionalId?: number
    partnerId?: number
    externalProfessionalName?: string
    externalProfessionalCrm?: string
    externalClinicName?: string
    externalClinicAddress?: string
    externalClinicPhone?: string
    specialty: MedicalSpecialty
    urgency?: ReferralUrgency
    reason: string
    clinicalSummary?: string
    relevantExams?: string
    currentMedications?: string
    diagnosisId?: number
    referralDate?: string
    scheduledDate?: string
}

export interface UpdateReferralData {
    receivingProfessionalId?: number
    partnerId?: number | null
    externalProfessionalName?: string
    externalProfessionalCrm?: string
    externalClinicName?: string
    externalClinicAddress?: string
    externalClinicPhone?: string
    specialty?: MedicalSpecialty
    urgency?: ReferralUrgency
    status?: ReferralStatus
    reason?: string
    clinicalSummary?: string
    relevantExams?: string
    currentMedications?: string
    diagnosisId?: number
    scheduledDate?: string
    completedDate?: string
    feedback?: string
}

// ---------- MEDICAL CERTIFICATE ----------

export interface MedicalCertificate {
    id: number
    clientId: number
    professionalId: number
    professional?: Professional
    type: MedicalCertificateType
    issueDate: string
    startDate: string
    endDate?: string
    daysOff?: number
    includeIcd: boolean
    icdCodeId?: number
    icdCode?: IcdCode
    purpose?: string
    content: string
    consultationId?: number
    signedAt?: string
    signatureData?: any
    signatureValid?: boolean
    signedPdfUrl?: string
    printedAt?: string
    printCount: number
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateCertificateData {
    clientId: number
    companyId?: number
    professionalId: number
    type: MedicalCertificateType
    startDate: string
    endDate?: string
    daysOff?: number
    includeIcd?: boolean
    icdCodeId?: number
    purpose?: string
    content: string
    consultationId?: number
}

export interface UpdateCertificateData {
    type?: MedicalCertificateType
    startDate?: string
    endDate?: string
    daysOff?: number
    includeIcd?: boolean
    icdCodeId?: number
    purpose?: string
    content?: string
    consultationId?: number
}

// ---------- HEALTH PLAN ----------

export interface HealthPlan {
    id: number
    name: string
    ansCode?: string
    phone?: string
    email?: string
    website?: string
    organizationId: number
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface ClientHealthPlan {
    id: number
    clientId: number
    healthPlanId: number
    healthPlan?: HealthPlan
    cardNumber: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary: boolean
    createdAt: string
    updatedAt: string
}

export interface CreateClientHealthPlanData {
    clientId: number
    healthPlanId: number
    cardNumber: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary?: boolean
}

export interface UpdateClientHealthPlanData {
    healthPlanId?: number
    cardNumber?: string
    planType?: string
    validUntil?: string
    holderName?: string
    holderCpf?: string
    relationship?: string
    isPrimary?: boolean
}

// ---------- INFORMED CONSENT ----------

export interface InformedConsentTemplate {
    id: number
    name: string
    type: ConsentType
    specialty?: MedicalSpecialty
    content: string
    companyId?: number
    isSystem: boolean
    active: boolean
    createdAt: string
    updatedAt: string
}

export interface InformedConsent {
    id: number
    clientId: number
    professionalId: number
    professional?: Professional
    templateId?: number
    template?: InformedConsentTemplate
    type: ConsentType
    content: string
    orderId?: number
    patientSignedAt?: string
    patientSignatureData?: any
    guardianName?: string
    guardianCpf?: string
    guardianRelation?: string
    guardianSignedAt?: string
    guardianSignatureData?: any
    witnesses?: any
    revokedAt?: string
    revocationReason?: string
    companyId: number
    createdAt: string
    updatedAt: string
}

export interface CreateConsentData {
    clientId: number
    companyId: number
    professionalId: number
    templateId?: number
    type: ConsentType
    content: string
    orderId?: number
}

export interface UpdateConsentData {
    templateId?: number
    type?: ConsentType
    content?: string
    orderId?: number
}

// ---------- MEDICAL TIMELINE ----------

export interface MedicalTimelineEntry {
    id: number
    clientId: number
    eventType: TimelineEventType
    eventId: number
    eventDate: string
    title: string
    summary?: string
    professionalId?: number
    professional?: Professional
    companyId: number
    metadata?: any
    createdAt: string
}

// ---------- PRONTUÁRIO SUMMARY ----------

export interface ProntuarioSummary {
    clientId: number
    allergiesCount: number
    activeAllergiesCount: number
    diagnosesCount: number
    activeDiagnosesCount: number
    lastConsultationDate?: string
    lastEvolutionDate?: string
    recentEvents: MedicalTimelineEntry[]
}

// ==================== API FUNCTIONS ====================

// ---------- ALLERGY API ----------

export async function getAllergiesByClient(
    clientId: number
): Promise<ApiResponse<Allergy[]>> {
    return apiRequest<Allergy[]>(`allergies/client/${clientId}`, {})
}

export async function getAllergy(id: number): Promise<ApiResponse<Allergy>> {
    return apiRequest<Allergy>(`allergies/${id}`, {})
}

export async function createAllergy(
    data: CreateAllergyData
): Promise<ApiResponse<Allergy>> {
    return apiRequest<Allergy>('allergies', { data }, 'post')
}

export async function updateAllergy(
    id: number,
    data: UpdateAllergyData
): Promise<ApiResponse<Allergy>> {
    return apiRequest<Allergy>(`allergies/${id}`, { data }, 'patch')
}

export async function deleteAllergy(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`allergies/${id}`, {}, 'delete')
}

// ---------- PERSONAL HISTORY API ----------

export async function getPersonalHistoryByClient(
    clientId: number
): Promise<ApiResponse<PersonalHistory[]>> {
    return apiRequest<PersonalHistory[]>(`personal-history/client/${clientId}`, {})
}

export async function getPersonalHistory(
    id: number
): Promise<ApiResponse<PersonalHistory>> {
    return apiRequest<PersonalHistory>(`personal-history/${id}`, {})
}

export async function createPersonalHistory(
    data: CreatePersonalHistoryData
): Promise<ApiResponse<PersonalHistory>> {
    return apiRequest<PersonalHistory>('personal-history', { data }, 'post')
}

export async function updatePersonalHistory(
    id: number,
    data: UpdatePersonalHistoryData
): Promise<ApiResponse<PersonalHistory>> {
    return apiRequest<PersonalHistory>(`personal-history/${id}`, { data }, 'patch')
}

export async function deletePersonalHistory(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`personal-history/${id}`, {}, 'delete')
}

// ---------- FAMILY HISTORY API ----------

export async function getFamilyHistoryByClient(
    clientId: number
): Promise<ApiResponse<FamilyHistory[]>> {
    return apiRequest<FamilyHistory[]>(`family-history/client/${clientId}`, {})
}

export async function getFamilyHistory(
    id: number
): Promise<ApiResponse<FamilyHistory>> {
    return apiRequest<FamilyHistory>(`family-history/${id}`, {})
}

export async function createFamilyHistory(
    data: CreateFamilyHistoryData
): Promise<ApiResponse<FamilyHistory>> {
    return apiRequest<FamilyHistory>('family-history', { data }, 'post')
}

export async function updateFamilyHistory(
    id: number,
    data: UpdateFamilyHistoryData
): Promise<ApiResponse<FamilyHistory>> {
    return apiRequest<FamilyHistory>(`family-history/${id}`, { data }, 'patch')
}

export async function deleteFamilyHistory(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`family-history/${id}`, {}, 'delete')
}

// ---------- ANAMNESIS API ----------

export async function getAnamnesesByClient(
    clientId: number
): Promise<ApiResponse<Anamnesis[]>> {
    return apiRequest<Anamnesis[]>(`anamnesis/client/${clientId}`, {})
}

export async function getAnamnesis(id: number): Promise<ApiResponse<Anamnesis>> {
    return apiRequest<Anamnesis>(`anamnesis/${id}`, {})
}

export async function createAnamnesis(
    data: CreateAnamnesisData
): Promise<ApiResponse<Anamnesis>> {
    return apiRequest<Anamnesis>('anamnesis', { data }, 'post')
}

export async function updateAnamnesis(
    id: number,
    data: UpdateAnamnesisData
): Promise<ApiResponse<Anamnesis>> {
    return apiRequest<Anamnesis>(`anamnesis/${id}`, { data }, 'patch')
}

export async function deleteAnamnesis(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`anamnesis/${id}`, {}, 'delete')
}

export async function getAnamnesisTemplates(
    companyId: number
): Promise<ApiResponse<AnamnesisTemplate[]>> {
    return apiRequest<AnamnesisTemplate[]>(`anamnesis/templates`, {
        params: { companyId },
    })
}

// ---------- EVOLUTION API ----------

export async function getEvolutionsByClient(
    clientId: number
): Promise<ApiResponse<MedicalEvolution[]>> {
    return apiRequest<MedicalEvolution[]>(`evolutions/client/${clientId}`, {})
}

export async function getEvolution(
    id: number
): Promise<ApiResponse<MedicalEvolution>> {
    return apiRequest<MedicalEvolution>(`evolutions/${id}`, {})
}

export async function createEvolution(
    data: CreateEvolutionData
): Promise<ApiResponse<MedicalEvolution>> {
    return apiRequest<MedicalEvolution>('evolutions', { data }, 'post')
}

export async function updateEvolution(
    id: number,
    data: UpdateEvolutionData
): Promise<ApiResponse<MedicalEvolution>> {
    return apiRequest<MedicalEvolution>(`evolutions/${id}`, { data }, 'patch')
}

export async function deleteEvolution(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`evolutions/${id}`, {}, 'delete')
}

export async function signEvolution(
    id: number
): Promise<ApiResponse<MedicalEvolution>> {
    return apiRequest<MedicalEvolution>(`evolutions/${id}/sign`, {}, 'post')
}

// ---------- DIAGNOSIS API ----------

export async function getDiagnosesByClient(
    clientId: number
): Promise<ApiResponse<Diagnosis[]>> {
    return apiRequest<Diagnosis[]>(`diagnoses/client/${clientId}`, {})
}

export async function getDiagnosis(id: number): Promise<ApiResponse<Diagnosis>> {
    return apiRequest<Diagnosis>(`diagnoses/${id}`, {})
}

export async function createDiagnosis(
    data: CreateDiagnosisData
): Promise<ApiResponse<Diagnosis>> {
    return apiRequest<Diagnosis>('diagnoses', { data }, 'post')
}

export async function updateDiagnosis(
    id: number,
    data: UpdateDiagnosisData
): Promise<ApiResponse<Diagnosis>> {
    return apiRequest<Diagnosis>(`diagnoses/${id}`, { data }, 'patch')
}

export async function deleteDiagnosis(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`diagnoses/${id}`, {}, 'delete')
}

// ---------- REFERRAL API ----------

export async function getReferralsByClient(
    clientId: number
): Promise<ApiResponse<Referral[]>> {
    return apiRequest<Referral[]>(`referrals/client/${clientId}`, {})
}

export async function getReferral(id: number): Promise<ApiResponse<Referral>> {
    return apiRequest<Referral>(`referrals/${id}`, {})
}

export async function createReferral(
    data: CreateReferralData
): Promise<ApiResponse<Referral>> {
    return apiRequest<Referral>('referrals', { data }, 'post')
}

export async function updateReferral(
    id: number,
    data: UpdateReferralData
): Promise<ApiResponse<Referral>> {
    return apiRequest<Referral>(`referrals/${id}`, { data }, 'patch')
}

export async function deleteReferral(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`referrals/${id}`, {}, 'delete')
}

export async function updateReferralStatus(
    id: number,
    status: ReferralStatus,
    feedback?: string
): Promise<ApiResponse<Referral>> {
    return apiRequest<Referral>(
        `referrals/${id}/status`,
        { data: { status, feedback } },
        'patch'
    )
}

// ---------- CERTIFICATE API ----------

export async function getCertificatesByClient(
    clientId: number
): Promise<ApiResponse<MedicalCertificate[]>> {
    return apiRequest<MedicalCertificate[]>(`certificates/client/${clientId}`, {})
}

export async function getCertificate(
    id: number
): Promise<ApiResponse<MedicalCertificate>> {
    return apiRequest<MedicalCertificate>(`certificates/${id}`, {})
}

export async function createCertificate(
    data: CreateCertificateData
): Promise<ApiResponse<MedicalCertificate>> {
    return apiRequest<MedicalCertificate>('certificates', { data }, 'post')
}

export async function updateCertificate(
    id: number,
    data: UpdateCertificateData
): Promise<ApiResponse<MedicalCertificate>> {
    return apiRequest<MedicalCertificate>(`certificates/${id}`, { data }, 'patch')
}

export async function deleteCertificate(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`certificates/${id}`, {}, 'delete')
}

export async function printCertificate(
    id: number
): Promise<ApiResponse<{ url: string }>> {
    return apiRequest<{ url: string }>(`certificates/${id}/print`, {}, 'post')
}

export async function printCertificatePdf(id: number): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/certificates/${id}/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar atestado em PDF')

    const blob = await response.blob()
    const filename = `atestado-${id}.pdf`

    downloadBlob(blob, filename)
}

export async function printReferralPdf(id: number): Promise<void> {
    const baseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const token = getStoredToken() || ''

    const url = `${baseUrl}/referrals/${id}/pdf`

    const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
    })

    if (!response.ok) throw new Error('Erro ao exportar encaminhamento em PDF')

    const blob = await response.blob()
    const filename = `encaminhamento-${id}.pdf`

    downloadBlob(blob, filename)
}

// ---------- HEALTH PLAN API ----------

export async function getHealthPlansByOrganization(
    organizationId: number
): Promise<ApiResponse<HealthPlan[]>> {
    return apiRequest<HealthPlan[]>(`health-plans/organization/${organizationId}`, {})
}

export async function getClientHealthPlans(
    clientId: number
): Promise<ApiResponse<ClientHealthPlan[]>> {
    return apiRequest<ClientHealthPlan[]>(`health-plans/client/${clientId}`, {})
}

export async function createClientHealthPlan(
    data: CreateClientHealthPlanData
): Promise<ApiResponse<ClientHealthPlan>> {
    return apiRequest<ClientHealthPlan>('health-plans/client', { data }, 'post')
}

export async function updateClientHealthPlan(
    id: number,
    data: UpdateClientHealthPlanData
): Promise<ApiResponse<ClientHealthPlan>> {
    return apiRequest<ClientHealthPlan>(
        `health-plans/client/${id}`,
        { data },
        'patch'
    )
}

export async function deleteClientHealthPlan(
    id: number
): Promise<ApiResponse<void>> {
    return apiRequest<void>(`health-plans/client/${id}`, {}, 'delete')
}

// ---------- CONSENT API ----------

export async function getConsentsByClient(
    clientId: number
): Promise<ApiResponse<InformedConsent[]>> {
    return apiRequest<InformedConsent[]>(`consents/client/${clientId}`, {})
}

export async function getConsent(
    id: number
): Promise<ApiResponse<InformedConsent>> {
    return apiRequest<InformedConsent>(`consents/${id}`, {})
}

export async function createConsent(
    data: CreateConsentData
): Promise<ApiResponse<InformedConsent>> {
    return apiRequest<InformedConsent>('consents', { data }, 'post')
}

export async function updateConsent(
    id: number,
    data: UpdateConsentData
): Promise<ApiResponse<InformedConsent>> {
    return apiRequest<InformedConsent>(`consents/${id}`, { data }, 'patch')
}

export async function deleteConsent(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`consents/${id}`, {}, 'delete')
}

export async function getConsentTemplates(
    companyId: number
): Promise<ApiResponse<InformedConsentTemplate[]>> {
    return apiRequest<InformedConsentTemplate[]>(`consents/templates`, {
        params: { companyId },
    })
}

export async function signConsent(
    id: number,
    signatureData: any,
    guardianData?: {
        guardianName?: string
        guardianCpf?: string
        guardianRelation?: string
        guardianSignatureData?: any
    }
): Promise<ApiResponse<InformedConsent>> {
    return apiRequest<InformedConsent>(
        `consents/${id}/sign`,
        { data: { signatureData, ...guardianData } },
        'post'
    )
}

export async function revokeConsent(
    id: number,
    reason: string
): Promise<ApiResponse<InformedConsent>> {
    return apiRequest<InformedConsent>(
        `consents/${id}/revoke`,
        { data: { reason } },
        'post'
    )
}

export async function sendConsentSigningEmail(
    consentId: number
): Promise<ApiResponse<{ message: string }>> {
    return apiRequest<{ message: string }>(
        `consents/${consentId}/send-signing-email`,
        {},
        'post'
    )
}

// ---------- TIMELINE API ----------

export async function getTimelineByClient(
    clientId: number,
    params?: {
        eventType?: TimelineEventType
        startDate?: string
        endDate?: string
        page?: number
        limit?: number
    }
): Promise<ApiResponse<MedicalTimelineEntry[]>> {
    const response = await apiRequest<MedicalTimelineEntry[] | { data: MedicalTimelineEntry[]; total: number; limit: number; offset: number; hasMore: boolean }>(`timeline/client/${clientId}`, {
        params,
    })

    // Se a resposta for um objeto paginado, extrair o array data
    if (response.success && response.result && typeof response.result === 'object' && 'data' in response.result && Array.isArray((response.result as any).data)) {
        return {
            ...response,
            result: (response.result as any).data,
        }
    }

    // Garantir que sempre retorne um array
    if (response.success && !Array.isArray(response.result)) {
        return {
            ...response,
            result: [],
        }
    }

    return response as ApiResponse<MedicalTimelineEntry[]>
}

// ---------- ICD API ----------

export async function searchIcdCodes(
    query: string
): Promise<ApiResponse<IcdCode[]>> {
    return apiRequest<IcdCode[]>('icd/search', { params: { q: query } })
}

// ---------- PRONTUÁRIO SUMMARY API ----------

export async function getProntuarioSummary(
    clientId: number
): Promise<ApiResponse<ProntuarioSummary>> {
    return apiRequest<ProntuarioSummary>(`prontuario/summary/${clientId}`, {})
}

// ==================== LABEL HELPERS ====================

export const ALLERGY_TYPE_LABELS: Record<AllergyType, string> = {
    [AllergyType.MEDICATION]: 'Medicamento',
    [AllergyType.FOOD]: 'Alimento',
    [AllergyType.ENVIRONMENTAL]: 'Ambiental',
    [AllergyType.INSECT]: 'Inseto',
    [AllergyType.LATEX]: 'Látex',
    [AllergyType.CONTRAST]: 'Contraste',
    [AllergyType.OTHER]: 'Outro',
}

export const ALLERGY_SEVERITY_LABELS: Record<AllergySeverity, string> = {
    [AllergySeverity.MILD]: 'Leve',
    [AllergySeverity.MODERATE]: 'Moderada',
    [AllergySeverity.SEVERE]: 'Grave',
    [AllergySeverity.LIFE_THREATENING]: 'Risco de Vida',
}

export const ALLERGY_SEVERITY_COLORS: Record<AllergySeverity, string> = {
    [AllergySeverity.MILD]: 'badge-success',
    [AllergySeverity.MODERATE]: 'badge-warning',
    [AllergySeverity.SEVERE]: 'badge-error',
    [AllergySeverity.LIFE_THREATENING]: 'badge-error bg-red-700',
}

export const ALLERGY_STATUS_LABELS: Record<AllergyStatus, string> = {
    [AllergyStatus.ACTIVE]: 'Ativa',
    [AllergyStatus.INACTIVE]: 'Inativa',
    [AllergyStatus.RESOLVED]: 'Resolvida',
    [AllergyStatus.REFUTED]: 'Refutada',
}

export const PERSONAL_HISTORY_CATEGORY_LABELS: Record<
    PersonalHistoryCategory,
    string
> = {
    [PersonalHistoryCategory.CARDIOVASCULAR]: 'Cardiovascular',
    [PersonalHistoryCategory.RESPIRATORY]: 'Respiratório',
    [PersonalHistoryCategory.NEUROLOGICAL]: 'Neurológico',
    [PersonalHistoryCategory.ENDOCRINE]: 'Endócrino',
    [PersonalHistoryCategory.GASTROINTESTINAL]: 'Gastrointestinal',
    [PersonalHistoryCategory.RENAL]: 'Renal',
    [PersonalHistoryCategory.HEPATIC]: 'Hepático',
    [PersonalHistoryCategory.MUSCULOSKELETAL]: 'Musculoesquelético',
    [PersonalHistoryCategory.DERMATOLOGICAL]: 'Dermatológico',
    [PersonalHistoryCategory.PSYCHIATRIC]: 'Psiquiátrico',
    [PersonalHistoryCategory.ONCOLOGICAL]: 'Oncológico',
    [PersonalHistoryCategory.INFECTIOUS]: 'Infeccioso',
    [PersonalHistoryCategory.AUTOIMMUNE]: 'Autoimune',
    [PersonalHistoryCategory.SURGICAL]: 'Cirúrgico',
    [PersonalHistoryCategory.HOSPITALIZATION]: 'Internação',
    [PersonalHistoryCategory.TRAUMA]: 'Trauma',
    [PersonalHistoryCategory.GYNECOLOGICAL]: 'Ginecológico',
    [PersonalHistoryCategory.OBSTETRIC]: 'Obstétrico',
    [PersonalHistoryCategory.VACCINATION]: 'Vacinação',
    [PersonalHistoryCategory.TRANSFUSION]: 'Transfusão',
    [PersonalHistoryCategory.OTHER]: 'Outro',
}

export const CONDITION_STATUS_LABELS: Record<ConditionStatus, string> = {
    [ConditionStatus.ACTIVE]: 'Ativo',
    [ConditionStatus.CONTROLLED]: 'Controlado',
    [ConditionStatus.RESOLVED]: 'Resolvido',
    [ConditionStatus.IN_REMISSION]: 'Em Remissão',
}

export const FAMILY_RELATIONSHIP_LABELS: Record<FamilyRelationship, string> = {
    [FamilyRelationship.FATHER]: 'Pai',
    [FamilyRelationship.MOTHER]: 'Mãe',
    [FamilyRelationship.PATERNAL_GRANDFATHER]: 'Avô Paterno',
    [FamilyRelationship.PATERNAL_GRANDMOTHER]: 'Avó Paterna',
    [FamilyRelationship.MATERNAL_GRANDFATHER]: 'Avô Materno',
    [FamilyRelationship.MATERNAL_GRANDMOTHER]: 'Avó Materna',
    [FamilyRelationship.BROTHER]: 'Irmão',
    [FamilyRelationship.SISTER]: 'Irmã',
    [FamilyRelationship.SON]: 'Filho',
    [FamilyRelationship.DAUGHTER]: 'Filha',
    [FamilyRelationship.UNCLE]: 'Tio',
    [FamilyRelationship.AUNT]: 'Tia',
    [FamilyRelationship.COUSIN]: 'Primo(a)',
    [FamilyRelationship.OTHER]: 'Outro',
}

export const DIAGNOSIS_TYPE_LABELS: Record<DiagnosisType, string> = {
    [DiagnosisType.PRIMARY]: 'Principal',
    [DiagnosisType.SECONDARY]: 'Secundário',
    [DiagnosisType.ADMISSION]: 'Admissão',
    [DiagnosisType.DISCHARGE]: 'Alta',
}

export const DIAGNOSIS_STATUS_LABELS: Record<DiagnosisStatus, string> = {
    [DiagnosisStatus.DIAGNOSIS_ACTIVE]: 'Ativo',
    [DiagnosisStatus.DIAGNOSIS_RESOLVED]: 'Resolvido',
    [DiagnosisStatus.DIAGNOSIS_IN_REMISSION]: 'Em Remissão',
    [DiagnosisStatus.CHRONIC]: 'Crônico',
}

export const DIAGNOSIS_STATUS_COLORS: Record<DiagnosisStatus, string> = {
    [DiagnosisStatus.DIAGNOSIS_ACTIVE]: 'badge-error',
    [DiagnosisStatus.DIAGNOSIS_RESOLVED]: 'badge-success',
    [DiagnosisStatus.DIAGNOSIS_IN_REMISSION]: 'badge-warning',
    [DiagnosisStatus.CHRONIC]: 'badge-info',
}

export const DIAGNOSIS_CERTAINTY_LABELS: Record<DiagnosisCertainty, string> = {
    [DiagnosisCertainty.CONFIRMED]: 'Confirmado',
    [DiagnosisCertainty.PROVISIONAL]: 'Provisório',
    [DiagnosisCertainty.DIFFERENTIAL]: 'Diferencial',
    [DiagnosisCertainty.RULED_OUT]: 'Descartado',
}

export const REFERRAL_URGENCY_LABELS: Record<ReferralUrgency, string> = {
    [ReferralUrgency.ROUTINE]: 'Rotina',
    [ReferralUrgency.REFERRAL_PRIORITY]: 'Prioritario',
    [ReferralUrgency.REFERRAL_URGENT]: 'Urgente',
    [ReferralUrgency.REFERRAL_EMERGENCY]: 'Emergência',
}

export const REFERRAL_URGENCY_COLORS: Record<ReferralUrgency, string> = {
    [ReferralUrgency.ROUTINE]: 'badge-info',
    [ReferralUrgency.REFERRAL_PRIORITY]: 'badge-warning',
    [ReferralUrgency.REFERRAL_URGENT]: 'badge-error',
    [ReferralUrgency.REFERRAL_EMERGENCY]: 'badge-error bg-red-700',
}

export const REFERRAL_STATUS_LABELS: Record<ReferralStatus, string> = {
    [ReferralStatus.REFERRAL_PENDING]: 'Pendente',
    [ReferralStatus.REFERRAL_SCHEDULED]: 'Agendado',
    [ReferralStatus.REFERRAL_IN_PROGRESS]: 'Em Andamento',
    [ReferralStatus.REFERRAL_COMPLETED]: 'Concluído',
    [ReferralStatus.REFERRAL_CANCELLED]: 'Cancelado',
    [ReferralStatus.REFERRAL_NO_SHOW]: 'Não Compareceu',
}

export const CERTIFICATE_TYPE_LABELS: Record<MedicalCertificateType, string> = {
    [MedicalCertificateType.SICK_LEAVE]: 'Atestado Médico',
    [MedicalCertificateType.FITNESS]: 'Aptidão Física',
    [MedicalCertificateType.ACCOMPANIMENT]: 'Acompanhante',
    [MedicalCertificateType.SCHOOL]: 'Escolar',
    [MedicalCertificateType.SPORTS]: 'Esportivo',
    [MedicalCertificateType.TRAVEL]: 'Viagem',
    [MedicalCertificateType.WORK_RETURN]: 'Retorno ao Trabalho',
    [MedicalCertificateType.CERTIFICATE_OTHER]: 'Outro',
}

export const CONSENT_TYPE_LABELS: Record<ConsentType, string> = {
    [ConsentType.GENERAL_TREATMENT]: 'Tratamento Geral',
    [ConsentType.SURGICAL_PROCEDURE]: 'Procedimento Cirúrgico',
    [ConsentType.ANESTHESIA]: 'Anestesia',
    [ConsentType.BLOOD_TRANSFUSION]: 'Transfusão de Sangue',
    [ConsentType.CLINICAL_TRIAL]: 'Pesquisa Clínica',
    [ConsentType.DATA_SHARING]: 'Compartilhamento de Dados',
    [ConsentType.TELEMEDICINE]: 'Telemedicina',
    [ConsentType.IMAGING]: 'Exame de Imagem',
    [ConsentType.INVASIVE_PROCEDURE]: 'Procedimento Invasivo',
    [ConsentType.CONSENT_OTHER]: 'Outro',
}

export const TIMELINE_EVENT_TYPE_LABELS: Record<TimelineEventType, string> = {
    [TimelineEventType.TIMELINE_CONSULTATION]: 'Consulta',
    [TimelineEventType.TIMELINE_TRIAGE]: 'Triagem',
    [TimelineEventType.TIMELINE_EVOLUTION]: 'Evolução',
    [TimelineEventType.TIMELINE_PRESCRIPTION]: 'Prescrição',
    [TimelineEventType.TIMELINE_EXAM_REQUEST]: 'Solicitação de Exame',
    [TimelineEventType.TIMELINE_EXAM_RESULT]: 'Resultado de Exame',
    [TimelineEventType.TIMELINE_PROCEDURE]: 'Procedimento',
    [TimelineEventType.TIMELINE_DIAGNOSIS]: 'Diagnóstico',
    [TimelineEventType.TIMELINE_ALLERGY_REGISTERED]: 'Alergia Registrada',
    [TimelineEventType.TIMELINE_REFERRAL]: 'Encaminhamento',
    [TimelineEventType.TIMELINE_CERTIFICATE]: 'Atestado',
    [TimelineEventType.TIMELINE_REPORT]: 'Laudo',
    [TimelineEventType.TIMELINE_DOCUMENT_SIGNED]: 'Documento Assinado',
    [TimelineEventType.TIMELINE_HOSPITALIZATION]: 'Internação',
    [TimelineEventType.TIMELINE_DISCHARGE]: 'Alta',
    [TimelineEventType.TIMELINE_VACCINATION]: 'Vacinação',
    [TimelineEventType.TIMELINE_NOTE]: 'Anotação',
}

export const MEDICAL_SPECIALTY_LABELS: Record<MedicalSpecialty, string> = {
    [MedicalSpecialty.GENERAL_PRACTICE]: 'Clínica Geral',
    [MedicalSpecialty.CARDIOLOGY]: 'Cardiologia',
    [MedicalSpecialty.DERMATOLOGY]: 'Dermatologia',
    [MedicalSpecialty.ENDOCRINOLOGY]: 'Endocrinologia',
    [MedicalSpecialty.GASTROENTEROLOGY]: 'Gastroenterologia',
    [MedicalSpecialty.GERIATRICS]: 'Geriatria',
    [MedicalSpecialty.GYNECOLOGY]: 'Ginecologia',
    [MedicalSpecialty.OBSTETRICS]: 'Obstetrícia',
    [MedicalSpecialty.HEMATOLOGY]: 'Hematologia',
    [MedicalSpecialty.NEPHROLOGY]: 'Nefrologia',
    [MedicalSpecialty.NEUROLOGY]: 'Neurologia',
    [MedicalSpecialty.ONCOLOGY]: 'Oncologia',
    [MedicalSpecialty.OPHTHALMOLOGY]: 'Oftalmologia',
    [MedicalSpecialty.ORTHOPEDICS]: 'Ortopedia',
    [MedicalSpecialty.OTOLARYNGOLOGY]: 'Otorrinolaringologia',
    [MedicalSpecialty.PEDIATRICS]: 'Pediatria',
    [MedicalSpecialty.PNEUMOLOGY]: 'Pneumologia',
    [MedicalSpecialty.PSYCHIATRY]: 'Psiquiatria',
    [MedicalSpecialty.RHEUMATOLOGY]: 'Reumatologia',
    [MedicalSpecialty.UROLOGY]: 'Urologia',
    [MedicalSpecialty.ANESTHESIOLOGY]: 'Anestesiologia',
    [MedicalSpecialty.RADIOLOGY]: 'Radiologia',
    [MedicalSpecialty.PATHOLOGY]: 'Patologia',
    [MedicalSpecialty.EMERGENCY]: 'Emergência',
    [MedicalSpecialty.INTENSIVE_CARE]: 'Terapia Intensiva',
    [MedicalSpecialty.SURGERY_GENERAL]: 'Cirurgia Geral',
    [MedicalSpecialty.SURGERY_CARDIOVASCULAR]: 'Cirurgia Cardiovascular',
    [MedicalSpecialty.SURGERY_PLASTIC]: 'Cirurgia Plástica',
    [MedicalSpecialty.SURGERY_VASCULAR]: 'Cirurgia Vascular',
    [MedicalSpecialty.PHYSICAL_THERAPY]: 'Fisioterapia',
    [MedicalSpecialty.NUTRITION]: 'Nutrição',
    [MedicalSpecialty.PSYCHOLOGY]: 'Psicologia',
    [MedicalSpecialty.SPEECH_THERAPY]: 'Fonoaudiologia',
    [MedicalSpecialty.OCCUPATIONAL_THERAPY]: 'Terapia Ocupacional',
    [MedicalSpecialty.DENTISTRY]: 'Odontologia',
    [MedicalSpecialty.OTHER]: 'Outro',
}
